import Button from '@jetbrains/ring-ui/components/button/button'
import Link from '@jetbrains/ring-ui/components/link/link'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import React from 'react'

import httpsConfigurationApi from '../../../HttpsConfigurationPage.api'
import {useDomainInfo} from '../../../HttpsConfigurationPage.hooks'

import styles from './TermsOfServiceForm.css'

import SvgIcon from 'src/components/common/SvgIcon/SvgIcon'
import {resolveRelative} from 'src/utils/url'

type DomainErrorProps = {
  type: 'server' | 'artifacts'
  isLocalhost?: boolean
}
function DomainError({type, isLocalhost}: DomainErrorProps) {
  return (
    <div className={styles.domainError}>
      <SvgIcon icon={'exception'} />
      {isLocalhost ? (
        <>
          {`The certificate cannot be issued for the ${type} domain `}
          <strong>{'localhost'}</strong>
        </>
      ) : (
        `Failed to resolve the ${type} domain`
      )}
      {'. '}
      {!isLocalhost && 'Consider '}
      <Link
        href={resolveRelative('/admin/admin.html?item=serverConfigGeneral')}
        target="_blank"
        rel="noreferrer"
      >
        {`${isLocalhost ? 'Change' : 'changing'} the ${type} URL`}
      </Link>
      {'.'}
    </div>
  )
}

// eslint-disable-next-line eqeqeq
const isDomainError = (domain?: string | null) => domain === 'localhost' || domain === null

function TermsOfServiceForm() {
  const {
    serverDomain,
    artifactsDomain,
    isLoading: isDomainInfoLoading,
    isFetching: isDomainInfoFetching,
  } = useDomainInfo('all')

  const isServerDomainError = isDomainError(serverDomain)
  const isArtifactsDomainError = isDomainError(artifactsDomain)

  const {terms, areTermsFetching} = httpsConfigurationApi.endpoints.getTermsOfService.useQuery(
    undefined,
    {
      selectFromResult: ({currentData, isFetching}) => ({
        terms: currentData,
        areTermsFetching: isFetching,
      }),
      skip: isServerDomainError || isArtifactsDomainError,
    },
  )

  const [fetchCertificate, {isLoading: fetchCertificateLoading}] =
    httpsConfigurationApi.endpoints.fetchCertificateFromAcme.useMutation({
      fixedCacheKey: 'fetchCertificateFromAcme',
    })

  if (isDomainInfoLoading) {
    return <LoaderInline>{'Loading...'}</LoaderInline>
  }

  return (
    <div
      className={classNames(styles.termsContainer, {
        [styles.overlay]: isDomainInfoFetching || areTermsFetching,
      })}
    >
      {isServerDomainError || isArtifactsDomainError ? (
        <div className={styles.errorBlock}>
          {isServerDomainError && (
            <DomainError type={'server'} isLocalhost={serverDomain === 'localhost'} />
          )}
          {isArtifactsDomainError && (
            <DomainError type={'artifacts'} isLocalhost={artifactsDomain === 'localhost'} />
          )}
        </div>
      ) : (
        <>
          <div className={styles.domainInfo}>
            {'A certificate will be issued for the server domain '}
            <strong>{serverDomain}</strong>
            {'.'}
            {artifactsDomain !== '' && (
              <>
                <br />
                {'Additional certificate will be issued for the artifacts domain '}
                <strong>{artifactsDomain}</strong>
                {'.'}
              </>
            )}
          </div>
          <div className={styles.terms}>
            {terms != null && (
              <>
                {'Read the '}
                <Link href={terms} target="_blank" rel="noreferrer">
                  {`Terms of Service`}
                </Link>
                {'.'}
              </>
            )}
          </div>
          <Button
            onClick={fetchCertificate}
            loader={fetchCertificateLoading}
            disabled={terms == null}
            primary
          >
            {'Agree and fetch'}
          </Button>
        </>
      )}
    </div>
  )
}

export default TermsOfServiceForm
